import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import { SetupStoreId } from '@/enum';
import { useRouterPush } from '@/hooks/common/router';
import { fetchGetUserInfo, fetchLogin } from '@/service/api';
import { localStg } from '@/utils/storage';
import { $t } from '@/locales';
import { useRouteStore } from '../route';
import { clearAuthStorage, getToken, getUserInfo } from './shared';

export const useAuthStore = defineStore(SetupStoreId.Auth, () => {
  const routeStore = useRouteStore();
  const { route, toLogin, redirectFromLogin } = useRouterPush(false);
  const { loading: loginLoading, startLoading, endLoading } = useLoading();

  const token = ref(getToken());
 
  const userInfo: Api.Auth.UserInfo = reactive(getUserInfo());
 
  // NEW: Overlay and guest mode state
  const showLoginOverlay = ref(false);
  const showGuestBanner = ref(false);
  const originalRoute = ref<string>('');

  /** is super role in static route */
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return VITE_AUTH_ROUTE_MODE === 'static' && userInfo.roles.includes(VITE_STATIC_SUPER_ROLE);
  });

  /** Is login */
  const isLogin = computed(() => Boolean(token.value));

  /** Reset auth store */
  async function resetStore() {
    const authStore = useAuthStore();

    // Preserve the guest-facing overlay/banner state across the reset. A
    // background auth failure (e.g. the App.vue ping returning 403 for a guest
    // on a fresh page load) calls resetStore(); the subsequent `$reset()` would
    // otherwise wipe `showLoginOverlay`/`showGuestBanner` back to their initial
    // `false`, closing the LoginOverlay that the route guard just opened. That
    // caused the overlay to flash open then immediately close on a hard refresh
    // of guest-viewable routes (e.g. /support/*), while soft navigation — which
    // does not re-ping — was unaffected.
    const preservedOverlayVisibility = authStore.showLoginOverlay;
    const preservedGuestBanner = authStore.showGuestBanner;
    const preservedOriginalRoute = authStore.originalRoute;

    clearAuthStorage();

    authStore.$reset();

    // Restore guest overlay state so a background reset never dismisses it.
    authStore.showLoginOverlay = preservedOverlayVisibility;
    authStore.showGuestBanner = preservedGuestBanner;
    authStore.originalRoute = preservedOriginalRoute;

    if (!route.value.meta.constant) {
      await toLogin();
    }

    routeStore.resetStore();
  }

  /**
   * Login
   *
   * @param userName User name
   * @param password Password
   * @param [redirect=true] Whether to redirect after login. Default is `true`
   */
  async function login(userName: string, password: string, redirect = true) {
    startLoading();

    const { data: loginToken, error } = await fetchLogin(userName, password);

    if (!error) {
      const pass = await loginByToken(loginToken);

      if (pass) {
        await routeStore.initAuthRoute();

        if (redirect) {
          await redirectFromLogin();
        }

        if (routeStore.isInitAuthRoute) {
          window.$notification?.success({
            title: $t('page.login.common.loginSuccess'),
            content: $t('page.login.common.welcomeBack', { userName: userInfo.userName }),
            duration: 4500
          });
        }
      }
    } else {
      resetStore();
    }

    endLoading();
  }

  async function loginByToken(loginToken: Api.Auth.LoginToken) {
    // 1. stored in the localStorage, the later requests need it in headers
    localStg.set('token', loginToken.token);
    localStg.set('refreshToken', loginToken.refreshToken);

    const { data: info, error } = await fetchGetUserInfo();

    if (!error) {
      // 2. store user info
      localStg.set('userInfo', info);

      // 3. update store
      token.value = loginToken.token;
      Object.assign(userInfo, info);

      return true;
    }

    return false;
    }
   
    // NEW: Guest mode and overlay management
    const setOverlayVisibility = (visible: boolean) => {
    showLoginOverlay.value = visible;
    };
   
    const setGuestBannerVisibility = (visible: boolean) => {
    showGuestBanner.value = visible;
    };
   
    const setOriginalRoute = (route: string) => {
    originalRoute.value = route;
    };
   
    // Enhanced login function for overlay system
    async function loginWithRedirect(redirect = true) {
    startLoading();
    
    // Use stored original route or current route
    const redirectPath = originalRoute.value || (redirect ? route.value.fullPath : '/home');
    
    // Clear overlay state
    showLoginOverlay.value = false;
    showGuestBanner.value = false;
    
    // Redirect to external auth
    window.location.href = `/chat?redirect=/platform${redirectPath}&show=login`;
    
    endLoading();
    }
   
    return {
    token,
    userInfo,
    isStaticSuper,
    isLogin,
    loginLoading,
    resetStore,
    login,
    // NEW: Guest mode and overlay exports
    showLoginOverlay,
    showGuestBanner,
    originalRoute,
    setOverlayVisibility,
    setGuestBannerVisibility,
    setOriginalRoute,
    loginWithRedirect
    };
   });
